import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LoginModal from "../components/LoginModal";
import useAuth from "../hooks/useAuth";

const INITIAL_DELAY_MS = 5 * 1000; // First appearance after 10 seconds
const DISMISS_COOLDOWN_MS = 30 * 1000; // Show again 20s after dismissal
// const DISMISS_COOLDOWN_MS = 1  * 1000; // Test
const CHECK_INTERVAL_MS = 5 * 1000; // Polling interval: every 5s

const LoginPromptContext = createContext(null);
const LS_KEY_LAST_SHOWN = "loginPromptLastShown";
const BLOCKED_PATHS = [
  "/login", 
  "/sign-up", 
  "/verify-email", 
  "/reset-password", 
  "/forgot-password"
]; // ✳️ Do not show modal on these authentication pages

function shouldShowPrompt() {
  try {
    const last = Number(localStorage.getItem(LS_KEY_LAST_SHOWN) || 0);
    return Date.now() - last > DISMISS_COOLDOWN_MS;
  } catch {
    return true;
  }
}

export default function LoginPromptProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState(
    "You must be logged in to access the full features of the website. Please log in to continue."
  );

  const navigate = useNavigate();
  const location = useLocation();

  const isBlockedPath = BLOCKED_PATHS.includes(location.pathname); // ⛔️ skip if on login/signup

  const showLoginPrompt = useCallback(() => {
    if (!isAuthenticated && shouldShowPrompt() && !isBlockedPath) {
      setIsOpen(true);
    }
  }, [isAuthenticated, isBlockedPath]);

  // ⏳ Initial 10s timer
  useEffect(() => {
    if (!isAuthenticated && !isBlockedPath) {
      const timer = setTimeout(() => {
        showLoginPrompt();
      }, INITIAL_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isBlockedPath, showLoginPrompt]);

  // 🔁 Polling to re-show modal after cooldown
  useEffect(() => {
    if (!isAuthenticated && !isBlockedPath) {
      const interval = setInterval(() => {
        if (!isOpen && shouldShowPrompt()) {
          setIsOpen(true);
        }
      }, CHECK_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isOpen]);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    try {
      localStorage.setItem(LS_KEY_LAST_SHOWN, String(Date.now()));
    } catch {}
  }, []);

  const handleLogin = useCallback(() => {
    handleDismiss();
    setIsOpen(false);
    const from = location.pathname + location.search;
    navigate(`/login`);
  }, [location, navigate]);

  const value = useMemo(() => ({}), []);

  return (
    <LoginPromptContext.Provider value={value}>
      {children}
      <LoginModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onLogin={handleLogin}
        message={message}
        onDismiss={handleDismiss}
      />
    </LoginPromptContext.Provider>
  );
}

export function useLoginPrompt() {
  const ctx = useContext(LoginPromptContext);
  if (!ctx)
    throw new Error("useLoginPrompt must be used inside LoginPromptProvider");
  return ctx;
}
